/**
 * Sending Identities Service (Fase 2 - Email Deliverability)
 *
 * Cubre los endpoints REST de SendingIdentity en el backend:
 *   /api/communications/sending-identities/
 *
 * Mirror de quotesService: objeto con metodos tipados, usa apiClient + API_ENDPOINTS.
 */

import { apiClient } from '@/lib/api-client';
import { API_ENDPOINTS } from '@/config/api.config';
import type { PaginatedResponse } from './leadsService';

// =====================================================================
// Types
// =====================================================================

export type SendingIdentityStatus =
  | 'pending'
  | 'pending_dns'
  | 'verifying'
  | 'verified'
  | 'failed'
  | 'disabled';

export type IdentityType = 'domain' | 'email_address' | 'delegated_subdomain';

export type DnsProvider =
  | 'cloudflare'
  | 'godaddy'
  | 'namecheap'
  | 'route53'
  | 'hostinger'
  | 'wix'
  | 'squarespace'
  | 'wordpress'
  | 'bluehost'
  | 'other'
  | '';

export type IdentityPurpose = 'transactional' | 'marketing';

export interface DnsRecord {
  type: string; // CNAME | MX | TXT
  host: string;
  value: string;
}

export interface DnsRecordsBundle {
  dkim?: DnsRecord[];
  mail_from?: DnsRecord[];
  dmarc_suggested?: DnsRecord;
}

export interface SendingIdentity {
  id: string;
  company: string | null;
  company_name: string | null;
  from_email: string;
  from_name: string;
  domain: string;
  identity_type: IdentityType;
  purpose: IdentityPurpose;
  verification_status: SendingIdentityStatus;
  is_default: boolean;
  is_platform_default: boolean;
  is_fallback_subdomain: boolean;
  is_managed_by_owly: boolean;
  verified_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SendingIdentityDetail extends SendingIdentity {
  mail_from_domain: string;
  ses_identity_arn: string;
  ses_configuration_set: string;
  region: string;
  dkim_tokens: string[];
  dns_records: DnsRecordsBundle;
  verification_requested_at: string | null;
  last_verification_check_at: string | null;
  disabled_at: string | null;
  disabled_reason: string;
  // Subdominio delegado
  parent_domain: string;
  subdomain_label: string;
  route53_hosted_zone_id: string;
  nameservers: string[];
  dns_provider: DnsProvider;
  // Delegacion del setup a IT
  delegate_email: string;
  delegate_sender_name: string;
  delegate_message: string;
  delegate_sent_at: string | null;
  delegate_token_expires_at: string | null;
  // Soft delete
  archived_at: string | null;
  _warnings?: string[];
}

export interface ProvisionFallbackInput {
  company_id: string;
}

export interface ProvisionCustomInput {
  company_id?: string | null;
  /**
   * Forma nueva (Fase 3 — wizard reordenado): solo `domain`.
   * El backend autogenera `from_email='noreply@<domain>'`.
   */
  domain?: string;
  /**
   * Forma legacy: si se manda, el backend deriva `domain` de aca.
   * Cuando se manda solo, el wizard salta el paso de form previo a verificar DNS.
   */
  from_email?: string;
  from_name?: string;
  identity_type?: IdentityType;
  set_default?: boolean;
}

export interface CustomizeIdentityInput {
  from_email?: string;
  from_name?: string;
}

export interface CheckResult {
  old_status: SendingIdentityStatus;
  new_status: SendingIdentityStatus;
  identity: SendingIdentityDetail;
}

export interface ForceVerifiedResult {
  old_status: SendingIdentityStatus;
  new_status: SendingIdentityStatus;
  reason: string;
  identity: SendingIdentityDetail;
}

export interface TestSendResult {
  accepted: boolean;
  message_id: string | null;
  error: string | null;
}

export interface HealthReport {
  overall: 'healthy' | 'degraded' | 'unhealthy';
  db_status: SendingIdentityStatus;
  ses_api_status: {
    verified: boolean | null;
    dkim_status: string | null;
    error: string | null;
  };
  dns_resolves: {
    dkim: boolean | null;
    mail_from: boolean | null;
    errors: string[];
  };
  last_send: { at: string; success: boolean; note?: string } | null;
  issues: string[];
}

export interface DestroyOptions {
  /** Soft (default) = solo disable. hard = elimina la fila. */
  hard?: boolean;
  /** Llama Route53 unpublish (zona gestionada por OWLY). */
  unpublishDns?: boolean;
  /** Llama SES DeleteEmailIdentity (irreversible). */
  deleteSes?: boolean;
  /** Reason guardado en disabled_reason. */
  reason?: string;
}

export interface PrecheckInput {
  domain: string;
  company_id?: string | null;
}

export interface PrecheckResult {
  valid: boolean;
  domain: string;
  format_ok: boolean;
  dns_resolves: boolean;
  available: boolean;
  provider: DnsProvider | null;
  ns_records: string[];
  suggested_subdomain: string;
  suggestions: string[];
  errors: string[];
  warnings: string[];
  /** Si la misma company ya tiene una identity en curso para este dominio,
   * el backend devuelve el id + status para que el wizard pueda saltar al
   * paso correspondiente sin re-crear nada. */
  existing_identity_id: string | null;
  existing_status: SendingIdentityStatus | null;
  existing_subdomain_label: string | null;
  /** False cuando el provider DNS detectado no permite agregar registros NS
   * (Hostinger, Wix, Squarespace, etc). En ese caso el wizard muestra una
   * pantalla explicativa con alternativas (subdominio OWLY o migrar a Cloudflare). */
  supports_ns_records: boolean;
  /** Mensaje humano explicando la limitacion (vacio si supports_ns_records=true). */
  ns_limitation_message: string;
}

export interface ProvisionDelegatedInput {
  company_id?: string | null;
  domain: string;
  subdomain_label?: string;
  dns_provider?: DnsProvider;
}

export interface DelegateSetupInput {
  email: string;
  name?: string;
  message?: string;
}

export interface DelegateSetupResult {
  sent: boolean;
  sent_to: string;
  expires_at: string;
  identity: SendingIdentityDetail;
}

export interface SetupTokenInfo {
  identity_id: string;
  status: SendingIdentityStatus;
  company_name: string;
  parent_domain: string;
  subdomain_label: string;
  fqdn: string;
  nameservers: string[];
  dns_provider: DnsProvider;
  requester: { name: string; message: string };
  expires_at: string | null;
  last_checked_at: string | null;
  verified_at: string | null;
  error: string;
}

// =====================================================================
// Service
// =====================================================================

const ENDPOINTS = API_ENDPOINTS.EMAIL_DELIVERABILITY.SENDING_IDENTITIES;

const customizeEndpoint = (id: string) =>
  `/communications/sending-identities/${id}/customize/`;

function buildQueryString(params?: Record<string, unknown>): string {
  if (!params) return '';
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((v) => qs.append(key, String(v)));
    } else {
      qs.append(key, String(value));
    }
  });
  const str = qs.toString();
  return str ? `?${str}` : '';
}

export const sendingIdentitiesService = {
  /** GET /sending-identities/ — list (filtrado por company auto en backend) */
  async list(params?: Record<string, unknown>): Promise<PaginatedResponse<SendingIdentity>> {
    return apiClient.get<PaginatedResponse<SendingIdentity>>(
      `${ENDPOINTS.BASE}${buildQueryString(params)}`,
    );
  },

  /** GET /sending-identities/<id>/ — detalle con dns_records */
  async retrieve(id: string): Promise<SendingIdentityDetail> {
    return apiClient.get<SendingIdentityDetail>(ENDPOINTS.DETAIL(id));
  },

  /** POST /sending-identities/provision-fallback/ — STAFF only */
  async provisionFallback(input: ProvisionFallbackInput): Promise<SendingIdentityDetail> {
    return apiClient.post<SendingIdentityDetail>(ENDPOINTS.PROVISION_FALLBACK, input);
  },

  /** POST /sending-identities/provision-custom/ — STAFF only */
  async provisionCustom(input: ProvisionCustomInput): Promise<SendingIdentityDetail> {
    return apiClient.post<SendingIdentityDetail>(ENDPOINTS.PROVISION_CUSTOM, input);
  },

  /** POST /sending-identities/precheck/ — valida dominio raiz para subdominio delegado */
  async precheck(input: PrecheckInput): Promise<PrecheckResult> {
    return apiClient.post<PrecheckResult>(ENDPOINTS.PRECHECK, input);
  },

  /** POST /sending-identities/provision-delegated/ — crea hosted zone + identity SES (idempotente) */
  async provisionDelegated(input: ProvisionDelegatedInput): Promise<SendingIdentityDetail> {
    return apiClient.post<SendingIdentityDetail>(ENDPOINTS.PROVISION_DELEGATED, input);
  },

  /** POST /sending-identities/<id>/delegate/ — manda email al tecnico con token */
  async delegate(id: string, input: DelegateSetupInput): Promise<DelegateSetupResult> {
    return apiClient.post<DelegateSetupResult>(ENDPOINTS.DELEGATE(id), input);
  },

  /** POST /sending-identities/<id>/check/ — re-consulta SES y refresca status */
  async check(id: string): Promise<CheckResult> {
    return apiClient.post<CheckResult>(ENDPOINTS.CHECK(id));
  },

  /** POST /sending-identities/<id>/force-verified/ — STAFF only (bypass lag SES) */
  async forceVerified(id: string, reason?: string): Promise<ForceVerifiedResult> {
    return apiClient.post<ForceVerifiedResult>(ENDPOINTS.FORCE_VERIFIED(id), { reason });
  },

  /** POST /sending-identities/<id>/test-send/ — envia correo de prueba real */
  async testSend(id: string, toEmail: string): Promise<TestSendResult> {
    return apiClient.post<TestSendResult>(ENDPOINTS.TEST_SEND(id), { to_email: toEmail });
  },

  /** GET /sending-identities/<id>/health/ — diagnostico DB+SES+DNS+last send */
  async health(id: string): Promise<HealthReport> {
    return apiClient.get<HealthReport>(ENDPOINTS.HEALTH(id));
  },

  /** POST /sending-identities/<id>/disable/ */
  async disable(id: string, reason?: string): Promise<SendingIdentityDetail> {
    return apiClient.post<SendingIdentityDetail>(ENDPOINTS.DISABLE(id), { reason });
  },

  /**
   * POST /sending-identities/<id>/customize/ — edita from_email y/o from_name.
   * Restriccion: el from_email nuevo debe estar bajo el mismo dominio verificado.
   * Util en el wizard reordenado: el cliente verifica el dominio primero, despues
   * personaliza el email exacto (noreply@..., contacto@..., admin@..., etc).
   */
  async customize(id: string, payload: CustomizeIdentityInput): Promise<SendingIdentityDetail> {
    return apiClient.post<SendingIdentityDetail>(customizeEndpoint(id), payload);
  },

  /** DELETE /sending-identities/<id>/?hard=&delete_ses=&unpublish_dns= */
  async destroy(id: string, options: DestroyOptions = {}): Promise<SendingIdentityDetail | { detail: string; id: string; warnings?: string[] }> {
    const params: Record<string, string> = {};
    if (options.hard) params.hard = 'true';
    if (options.unpublishDns) params.unpublish_dns = 'true';
    if (options.deleteSes) params.delete_ses = 'true';
    if (options.reason) params.reason = options.reason;
    const qs = new URLSearchParams(params).toString();
    const url = qs ? `${ENDPOINTS.DETAIL(id)}?${qs}` : ENDPOINTS.DETAIL(id);
    return apiClient.delete<SendingIdentityDetail>(url);
  },
};
